import { formatBondValue } from "@/lib/bondValueDisplay";
import type { BondRenderField } from "@/lib/bondFields";

// BRK-S140 — a captured bond value, as a RECORD rather than as a form.
//
// Extracted from `NewBondSubmissionPage`, which has owned it since BRK-S106 for
// the "filed order, opened again" panel. The submission profile is the second
// surface rendering the same values, and what would have been copied is not the
// markup: it is the decision that an UNANSWERED field is OMITTED rather than
// dashed, and that the display hints come from the same field adapter the inputs
// use (`toRenderField` / `toRenderFinalField`), which is what keeps control and
// display agreeing about a field's type (D-10).

/** One label/value pair on the record. `value` is already a display string —
 *  the formatting decision belongs to `formatBondValue`, not to the markup. */
export function RecordField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-brk-dim">{label}</dt>
      <dd className="break-words font-medium text-brk-text">{value}</dd>
    </div>
  );
}

/**
 * A set of fields' captured values, in the order the CONFIGURATION asks for
 * them — so the record reads like the form the broker filled in rather than
 * like a bag.
 *
 * An unanswered field is OMITTED rather than dashed: a record padded with em
 * dashes for every optional field nobody filled in reads as a blank form. (The
 * account PROFILE takes the opposite line — see `orNotProvided` — and the
 * difference is deliberate: that page has a fixed eight facts a broker reads
 * down the phone, where this one has twenty-odd optional configuration fields.)
 */
export function CapturedValues({
  fields,
  values,
  emptyNote,
  extra,
}: {
  fields: readonly BondRenderField[];
  values: Record<string, unknown>;
  /**
   * What to say when NOTHING here was answered. Overridable because "nothing
   * captured" is the wrong sentence for a per-owner step whose primary owner
   * answered nothing (BRK-S109).
   */
  emptyNote?: string;
  /** Rendered after a field's value, keyed by field name — the profile's
   *  "View on map" link (BRK-S140). Returning null adds nothing. */
  extra?: (field: BondRenderField) => React.ReactNode;
}) {
  const captured = fields.flatMap((f) => {
    // The options ride along so an option-id value renders as its LABEL
    // ("LLC", never 1) — resolved generally from whatever field carries pairs,
    // not from a field-name special case (BRK-S107).
    const value = formatBondValue(values[f.name], {
      type: f.type,
      options: f.options,
    });
    return value === null ? [] : [{ field: f, value }];
  });
  if (captured.length === 0) {
    return (
      <p className="text-sm text-brk-dim">
        {emptyNote ?? "Nothing captured on this step."}
      </p>
    );
  }
  return (
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {captured.map((c) => (
        <RecordField
          key={c.field.name}
          label={c.field.label}
          value={
            <>
              {c.value}
              {extra?.(c.field)}
            </>
          }
        />
      ))}
    </dl>
  );
}
